import { NetworkAvailability } from '../NetworkAvailability';
import { appDebugWarn } from '../appDebugLog';

type LogLevel = 'info' | 'warn' | 'error';

interface LogPayload {
  level: LogLevel;
  message: string;
  screen?: string;
  metaJson?: string;
  timestamp?: string;
  path?: string;
  userAgent?: string;
  appName?: string;
  userId?: string;
  tenantId?: string;
}

interface UserContext {
  userId?: string;
  tenantId?: string;
  baseUrl?: string;
  token?: string;
}

const PENDING_LOGS_STORAGE_KEY = 'rp4s.clientLogger.pending';
const MAX_QUEUED_LOGS = 200;
const MAX_FLUSH_BACKOFF_MS = 60_000;

class ClientLogger {
  private static instance: ClientLogger;
  private appName = 'react-rp4s';
  private isEnabled = true; //process.env.REACT_APP_NODE_ENV === 'production';

  private queue: LogPayload[] = [];
  private flushInterval = 5000;
  private maxBatchSize = 10;
  private userContext: UserContext = {};
  private flushInFlight = false;
  private flushBackoffMs = 0;
  private nextFlushAllowedAt = 0;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private networkUnsubscribe: (() => void) | null = null;
  /** Avoid repeating the same flush-failure warning every interval tick. */
  private lastFlushWarnKey: string | null = null;

  private constructor() {
    if (!this.isEnabled) {
      return;
    }
    this.restorePendingLogs();
    this.flushTimer = setInterval(() => this.flush(), this.flushInterval);
    this.networkUnsubscribe = NetworkAvailability.subscribe((online) => {
      if (online) {
        this.flushBackoffMs = 0;
        this.nextFlushAllowedAt = 0;
        void this.flush();
      }
    });
  }

  public static getInstance(): ClientLogger {
    if (!ClientLogger.instance) {
      ClientLogger.instance = new ClientLogger();
    }
    return ClientLogger.instance;
  }

  public setUserContext(context: UserContext) {
    this.userContext = { ...this.userContext, ...context };
  }

  public info(message: string, screen?: string, meta?: Record<string, any>) {
    this.enqueue('info', message, screen, meta);
  }

  public warn(message: string, screen?: string, meta?: Record<string, any>) {
    this.enqueue('warn', message, screen, meta);
  }

  public error(message: string, screen?: string, meta?: Record<string, any>) {
    this.enqueue('error', message, screen, meta);
  }

  private enqueue(level: LogLevel, message: string, screen?: string, meta?: Record<string, any>) {
    if (!this.isEnabled) return;

    let screenStr: string | undefined;
    if (screen === undefined || screen === null) {
      screenStr = undefined;
    } else if (typeof screen === 'string') {
      screenStr = screen;
    } else if (typeof screen === 'number' || typeof screen === 'boolean') {
      screenStr = String(screen);
    } else {
      screenStr = undefined;
    }

    const payload: LogPayload = {
      level,
      message,
      screen: screenStr,
      metaJson: meta ? JSON.stringify(meta): "",
      appName: this.appName,
      timestamp: new Date().toISOString(),
      path: window.location.pathname,
      userAgent: navigator.userAgent,
      userId: this.userContext.userId,
      tenantId: this.userContext.tenantId
    };

    this.queue.push(payload);
    this.trimQueue();
    this.persistPendingLogs();
    if (this.queue.length >= this.maxBatchSize) {
      void this.flush();
    }
  }

  private async flush() {
    if (
      this.queue.length === 0 ||
      !this.isEnabled ||
      !this.userContext.baseUrl ||
      this.flushInFlight
    ) {
      return;
    }

    if (!NetworkAvailability.isOnline()) {
      return;
    }

    if (Date.now() < this.nextFlushAllowedAt) {
      return;
    }

    const logsToSend = this.queue.splice(0, this.maxBatchSize);
    this.persistPendingLogs();
    this.flushInFlight = true;

    const endpoint = `${this.userContext.baseUrl}/api/v1.0/insights/client-logs`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (this.userContext.token) {
      headers['Authorization'] = `Bearer ${this.userContext.token}`;
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(logsToSend)
      });

      if (response.status === 401 || response.status === 403) {
        // Stale/missing auth after sleep — keep logs, wait for token sync + online resume.
        this.requeueFront(logsToSend);
        this.scheduleFlushBackoff();
        this.warnOnce('unauthorized', '[ClientLogger] flush unauthorized — will retry after token/network recover');
        return;
      }

      if (!response.ok) {
        this.requeueFront(logsToSend);
        this.scheduleFlushBackoff();
        this.warnOnce(`http-${response.status}`, `[ClientLogger] flush HTTP ${response.status}`);
        return;
      }

      this.flushBackoffMs = 0;
      this.nextFlushAllowedAt = 0;
      this.lastFlushWarnKey = null;

      // Drain remaining queued items while online.
      if (this.queue.length > 0) {
        this.flushInFlight = false;
        void this.flush();
        return;
      }
    } catch (err) {
      this.requeueFront(logsToSend);
      this.scheduleFlushBackoff();
      if (NetworkAvailability.isNetworkError(err)) {
        this.warnOnce('network', '[ClientLogger] flush skipped — network unavailable');
      } else {
        this.warnOnce('error', '[ClientLogger] flush failed — will retry with backoff');
        appDebugWarn('[ClientLogger] flush error detail', err);
      }
    } finally {
      this.flushInFlight = false;
      this.persistPendingLogs();
    }
  }

  private warnOnce(key: string, message: string) {
    if (this.lastFlushWarnKey === key) {
      return;
    }
    this.lastFlushWarnKey = key;
    appDebugWarn(message);
  }

  private requeueFront(logs: LogPayload[]) {
    this.queue = [...logs, ...this.queue];
    this.trimQueue();
  }

  private trimQueue() {
    if (this.queue.length > MAX_QUEUED_LOGS) {
      this.queue = this.queue.slice(this.queue.length - MAX_QUEUED_LOGS);
    }
  }

  private scheduleFlushBackoff() {
    this.flushBackoffMs = this.flushBackoffMs
      ? Math.min(this.flushBackoffMs * 2, MAX_FLUSH_BACKOFF_MS)
      : 2000;
    this.nextFlushAllowedAt = Date.now() + this.flushBackoffMs;
  }

  private persistPendingLogs() {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }
      if (this.queue.length === 0) {
        localStorage.removeItem(PENDING_LOGS_STORAGE_KEY);
        return;
      }
      localStorage.setItem(PENDING_LOGS_STORAGE_KEY, JSON.stringify(this.queue));
    } catch {
      // quota / private mode — ignore
    }
  }

  private restorePendingLogs() {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }
      const raw = localStorage.getItem(PENDING_LOGS_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.queue = parsed.filter((item) => item && typeof item.message === 'string');
        this.trimQueue();
      }
    } catch {
      // ignore corrupt storage
    }
  }
}

export const logger = ClientLogger.getInstance();

/**
import { logger } from './utils/ClientLogger';
import { store } from './store';

const authUser = store.getState().auth.authUser;

logger.setUserContext({
  userId: authUser?.UserId,
  tenantId: authUser?.TenantCode,
  baseUrl: authUser?.RPAPIGW_URL,
  token: authUser?.AccessToken
});

logger.info('User visited dashboard', 'Dashboard', { section: 'Overview' });
 */
